<template>
  <div class="rpw">
    <p v-if="!pages.length" class="ov-empty">{{ $t('dash.overview.widgets.choose_page') }}</p>
    <p v-else-if="!eligiblePages.length" class="ov-empty">{{ $t('dash.overview.widgets.page_not_eligible', { name: pageNames(pages) }) }}</p>
    <div v-else-if="loading && !groups.length" class="rpw-skeleton"></div>
    <p v-else-if="error && !groups.length" class="ov-empty">{{ $t('dash.overview.error') }}</p>
    <p v-else-if="!groups.length" class="ov-empty">{{ $t('dash.overview.widgets.no_data') }}</p>
    <template v-else>
      <!-- Une section par réseau (les panneaux du Reporting sont propres à chaque réseau), pages du réseau combinées -->
      <div v-for="group in groups" :key="group.network" class="rpw-group">
        <p v-if="groups.length > 1" class="rpw-group-head">
          <span class="rpw-net" :class="`rpw-net--${group.network}`">{{ $t(`dash.overview.network.${group.network}`) }}</span>
          {{ pageNames(group.pages) }}
        </p>
        <p v-if="emptyPanels[group.network]" class="ov-empty">{{ $t('dash.overview.widgets.no_data') }}</p>
        <component
          v-show="!emptyPanels[group.network]"
          :ref="`panel-${group.network}`"
          :is="component"
          class="rpw-panel"
          :stats="group.stats"
          :page="group.page"
          :graph-labels="graphLabels(group.stats)"
          :date-start="dateStart"
          :date-end="dateEnd"
          :prev-loading="false"
          :solution-post-map="{}"
        />
      </div>
      <p v-if="skippedPages.length" class="rpw-skipped">{{ $t('dash.overview.widgets.pages_skipped', { names: pageNames(skippedPages) }) }}</p>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { isReportingEligible } from '@/utils/reportingEligibility'
import { apiGet, rangeParams } from './widgetFetch'
import { mergeStats } from './mergeStats'

const PANELS = {
  ReportingOverview: () => import('../../../reporting-web/ReportingOverview.vue'),
  ReportingVisibility: () => import('../../../reporting-web/ReportingVisibility.vue'),
  ReportingImpressions: () => import('../../../reporting-web/ReportingImpressions.vue'),
  ReportingEngagement: () => import('../../../reporting-web/ReportingEngagement.vue'),
  ReportingContent: () => import('../../../reporting-web/ReportingContent.vue'),
  ReportingPostTypes: () => import('../../../reporting-web/ReportingPostTypes.vue'),
  ReportingVideos: () => import('../../../reporting-web/ReportingVideos.vue'),
  ReportingStories: () => import('../../../reporting-web/ReportingStories.vue'),
  ReportingDemographics: () => import('../../../reporting-web/ReportingDemographics.vue'),
  ReportingConcurrent: () => import('../../../reporting-web/ReportingConcurrent.vue')
}
const CONCURRENCY = 3

/**
 * Panneau de la section Reporting pour une ou plusieurs pages du tableau de bord (stats ES, sans backfill).
 * Plusieurs pages d'un même réseau : stats combinées dans un seul panneau ; réseaux différents : un panneau par réseau.
 */
export default {
  name: 'ReportingPanelWidget',

  props: {
    panel: { type: String, required: true },
    pages: { type: Array, default: () => [] },
    range: { type: Object, required: true },
    refreshToken: { type: Number, default: 0 }
  },

  data() {
    return { results: {}, loading: false, error: false, requestId: 0, emptyPanels: {} }
  },

  computed: {
    component() {
      return PANELS[this.panel]
    },

    eligiblePages() {
      return this.pages.filter(isReportingEligible)
    },

    // Pages choisies mais hors Reporting (groupes, conciergerie seule…) : ignorées et signalées
    skippedPages() {
      return this.pages.filter(p => !isReportingEligible(p))
    },

    loadKey() {
      return `${this.eligiblePages.map(p => p.id).join(',')}|${this.range.since}|${this.range.until}`
    },

    groups() {
      const byNetwork = {}
      this.eligiblePages.forEach(page => {
        const stats = this.results[String(page.id)]
        if (stats && stats.page) (byNetwork[page.network] || (byNetwork[page.network] = [])).push({ page, stats })
      })
      return Object.entries(byNetwork).map(([network, list]) => {
        const pages = list.map(x => x.page)
        if (list.length === 1) return { network, pages, page: list[0].page, stats: list[0].stats }
        return {
          network,
          pages,
          // Page fictive : identifiant unique (graphiques), nom combiné
          page: { ...list[0].page, id: `merged-${pages.map(p => p.id).join('-')}`, name: this.pageNames(pages) },
          stats: mergeStats(list.map(x => x.stats))
        }
      })
    },

    // « Depuis le début » : 12 mois pour les panneaux qui affichent des dates
    dateStart() {
      return (this.range.since ? moment.unix(this.range.since) : moment().subtract(12, 'months')).format('YYYY-MM-DD')
    },

    dateEnd() {
      return (this.range.until ? moment.unix(this.range.until) : moment()).format('YYYY-MM-DD')
    }
  },

  watch: {
    loadKey() {
      this.load()
    },

    refreshToken() {
      this.load(true)
    }
  },

  created() {
    this.load()
  },

  updated() {
    this.checkEmpty()
  },

  methods: {
    pageNames(pages) {
      return pages.map(p => p.name).join(', ')
    },

    // Même construction que ReportingMain.loadSinglePage
    graphLabels(stats) {
      return ((stats && stats.page && stats.page.nb_post_graph) || [])
        .slice()
        .sort((a, b) => a.date - b.date)
        .map(p => moment(p.date * 1000).format('LL'))
    },

    // Les panneaux du Reporting se masquent (v-if) sans données : leur racine est alors un commentaire
    checkEmpty() {
      const next = {}
      this.groups.forEach(group => {
        const ref = this.$refs[`panel-${group.network}`]
        const panel = Array.isArray(ref) ? ref[0] : ref
        next[group.network] = !!(panel && panel.$el && panel.$el.nodeType === Node.COMMENT_NODE)
        // En-tête réduit à l'icône et au titre (masqués) : retiré ; gardé s'il porte des actions (filtres, ajout…)
        if (panel && panel.$el && panel.$el.querySelectorAll) {
          panel.$el.querySelectorAll('.section-header').forEach(header => {
            const onlyTitle = [...header.children].every(el => el.matches('.section-icon, h2, .prev-loading-badge'))
            header.classList.toggle('rpw-header-empty', onlyTitle)
          })
        }
      })
      if (JSON.stringify(next) !== JSON.stringify(this.emptyPanels)) this.emptyPanels = next
    },

    async load(refresh = false) {
      const ids = this.eligiblePages.map(p => String(p.id))
      const requestId = ++this.requestId
      if (!ids.length) {
        this.results = {}
        return
      }
      this.loading = true
      this.error = false
      const results = {}
      let failed = 0
      const queue = ids.slice()
      const worker = async () => {
        while (queue.length) {
          const id = queue.shift()
          try {
            // Stats volumineuses (posts) : pas de cache local
            const body = await apiGet(`/reporting/page-stats?${rangeParams(this.range, { pageid: id })}`, { refresh, persist: false })
            results[id] = body.stats
          } catch (e) {
            failed++
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker))
      if (requestId !== this.requestId) return
      this.results = results
      this.error = failed === ids.length
      this.loading = false
    }
  }
}
</script>

<style scoped>
.rpw {
  height: 100%;
  min-height: 0;
}
.rpw-skeleton {
  height: 100%;
  min-height: 120px;
  border-radius: 6px;
  background: #f1edf5;
}
.rpw-group + .rpw-group {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f1edf5;
}
.rpw-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: #555e75;
}
.rpw-net {
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 10.5px;
  font-weight: 700;
  background: #f1edf5;
  color: #6b1f4d;
}
.rpw-net--facebook { background: #e7f0fd; color: #1877f2; }
.rpw-net--instagram { background: #fde8f1; color: #c13584; }
.rpw-net--linkedin { background: #e6f1f8; color: #0a66c2; }
.rpw-net--tiktok { background: #eef0f3; color: #121826; }
.rpw-skipped {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: #9ba3b7;
}
/* Intégré dans la carte du widget : ni carte dans la carte, ni second titre (le titre est celui du widget) */
.rpw-panel {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}
.rpw-panel >>> .section-header > .section-icon,
.rpw-panel >>> .section-header > h2 {
  display: none;
}
.rpw-panel >>> .section-header {
  justify-content: flex-end;
  margin-bottom: 10px;
}
.rpw-panel >>> .section-header:empty,
.rpw-panel >>> .section-header.rpw-header-empty {
  display: none;
}
</style>
